import * as React from "react";
import { Tooltip } from "antd";
import { withTranslation, WithTranslation } from "react-i18next";
import { I18N_NS } from "Config/i18n";
import { UploadedTmpFile } from "./FileUploadStore";
import { hasBlacklistedFilenameChars, getFoundBlacklistedFilenameChars } from "utils/inputValidation";

interface ResolveExceptionProps extends WithTranslation {
  errors: API_UploadErrorInfo;
  filenamePattern?: string;
  fileName?: string;
  messages?: string[];
  indexErrorArgs?: API_IndexErrorArgs;
  children: React.ReactNode;
  getFileById: (id: number | string) => UploadedTmpFile;
}

export const ResolveException = withTranslation(I18N_NS.PROJECT)(
  class extends React.Component<ResolveExceptionProps> {
    // tslint:disable-next-line:max-line-length
    private handledExceptions: API_UPLOADERRORINFO_STATUS[] = [
      "RENAMEFILE",
      "REPLACE",
      "MISSINGMETADATUM",
      "FILENAMECONVENTION_FILENAME",
      "FILENAMECONVENTION_FILETYPE",
      "INIT",
      "INDEX",
      "DUPLICATE",
      "ERROR",
      "NO_PDF",
      "VIEW_NO_AUTOMAPPING",
      "LOCK",
      "MISSING_DESCRIPTION",
      "ZIP_DIRECTORY",
      "EMPTY_FILE",
      "DELETED_DIR",
      // "INVALID_TARGET"
    ];

    getDuplicateErrorMessages = () => {
      const { t } = this.props;
      const { messages = [] } = this.props.errors;
      const messagesConfig = {
        "DUPLICATE ID: ": t("Remove duplicate") + ":",
        "CONVENTION DUPLICATE ID: ": t("Filenameconvention discovered filename conflict with file:"),
      };
      const formattedMessages: string[] = [];
      messages.forEach((item) => {
        Object.keys(messagesConfig).forEach((message) => {
          if (item.startsWith(message)) {
            const id = +item.split(message)[1];
            const foundFile = this.props.getFileById(id);
            formattedMessages.push(`${messagesConfig[message]} ${foundFile ? foundFile.name : foundFile}`);
          }
        });
      });
      if (!formattedMessages.length) {
        return "";
      }
      if (formattedMessages.length > 1) {
        return formattedMessages.join(", ");
      }
      return formattedMessages[0];
    };

    renderMetadataHints = () => {
      const content = [];
      this.props.errors.missingMetadata?.forEach(({ description, name }, idx) => {
        if (idx < 3) {
          const text = description && description.length ? `${name} - ${description}` : `${name}`;
          content.push(text);
        }
      });
      content.push("...");
      return (
        <div>
          <div>{this.props.t("Missing metadata")}</div>
          {content.map((i) => (
            <div key={i}>{i}</div>
          ))}
        </div>
      );
    };

    getFilenameConventionHint = (): string => {
      const { t, fileName, filenamePattern } = this.props;
      if (fileName && hasBlacklistedFilenameChars(fileName)) {
        const chars = getFoundBlacklistedFilenameChars(fileName);
        return t("{{unallowedChars}} not allowed", {
          unallowedChars: chars,
          interpolation: { escapeValue: false },
        });
      }
      return t("Conflict with name convention:\n{{pattern}}", {
        pattern: filenamePattern,
        interpolation: { escapeValue: false },
      });
    };

    get resolveHints(): { [key in API_UPLOADERRORINFO_STATUS]?: string | React.ReactNode } {
      return {
        RENAMEFILE: this.props.t("Rename file"),
        // tslint:disable-next-line:max-line-length
        REPLACE:
          this.props.t(
            // eslint-disable-next-line max-len
            "Possibilities: choose a new name for the file, remove new file from upload, replace existing file by new one"
          ) + (this.props.messages?.length > 0 ? "\n " + this.props.messages[0] : ""),
        MISSINGMETADATUM: this.renderMetadataHints(),
        // 'CALLBACK_WHICHFILE': 'TODO: Not defined yet',
        // 'ERROR': 'TODO: Not defined yet',
        FILENAMECONVENTION_FILENAME: this.getFilenameConventionHint(),
        FILENAMECONVENTION_FILETYPE: this.props.t("Conflict with name convention:\n{{pattern}}", {
          pattern: this.props.filenamePattern,
          interpolation: { escapeValue: false },
        }),
        INDEX: this.props.messages[0],
        DUPLICATE: this.getDuplicateErrorMessages(),
        LOCK: this.props.messages ? this.props.messages[0] : this.props.t("File locked by another user"),
        ERROR: this.props.messages ? this.props.messages[0] : "Unknown exception (no message from back-end)",
        NO_PDF: this.props.t("Please upload together with the DWG a PDF version of that file"),
        INIT: "File not yet tested",
        ZIP_DIRECTORY: "You dont have acces rights to create folders",
        VIEW_NO_AUTOMAPPING: this.props.t("This file cannot be automapped"),
        MISSING_DESCRIPTION: this.props.messages
          ? this.props.messages[0]
          : this.props.t("The file description is mandatory. Please enter one."),
        EMPTY_FILE: this.props.messages[0],
        DELETED_DIR: this.props.messages[0],
        // INVALID_TARGET: this.props.t("This file cannot be automapped"),
      };
    }

    get isKnownException() {
      // filter exceptions that are not implemented
      if (!this.props.errors) {
        return false;
      }
      return this.handledExceptions.includes(this.props.errors.status);
    }

    get content() {
      const { errors } = this.props;
      if (!this.isKnownException) {
        return "Unknown exception";
      }
      return this.resolveHints[errors.status];
    }

    render() {
      return (
        <Tooltip title={this.content} placement="left">
          <span>{this.props.children}</span>
        </Tooltip>
      );
    }
  }
);
